import Modal from "../../UIelements/Modal";
import { useIntl } from 'react-intl';
import { ChangeEvent, FormEvent, useCallback, useEffect, useRef, useState } from "react";
import * as Styled from './styles';
import Loading from "../../Loading";
import PrimaryButton from "../../FormElements/PrimaryButton";
import InputForwardRef, { InputForwardType } from "../../FormElements/InputForwardRef";
import { validate, VALIDATOR_MAXLENGTH, VALIDATOR_REQUIRE } from "../../../utils/validators";
import { addNewStore, updateEquipment, loadCompanyById } from "../../../services";
import { toast } from 'react-toastify';
import { InputsType } from "../../../hooks/form-hook-typing";
import { useForm } from "../../../hooks/form-hook";
import { Equipment, UpdateEquipmentRequestType } from "../../../models/equipment";
import { AddStoreRequestType } from "../../../models/Stores";
import IconButton from "../../FormElements/IconButton";
import { useLiveQuery } from "dexie-react-hooks";
import { db } from "../../../db/db";
import { getCurrentDateTimezone } from "../../../utils/formatDateHour";
import { ReactComponent as Question } from '../../../assets/image/question.svg';
import InputChecked from "../../FormElements/InputChecked";
import ModalRemoteAccess from "../ModalRemoteAccess";
import Company from "../../../models/Company";
import { loadLocationsByCompanyId } from "../../../services/location";
import { loadStoresByCompanyId } from "../../../services/store";
import { ListType, Select } from "@praticabr/ppsa";

enum InputEquipmentNames {
  PIN = 'IOKPin',
  TYPE = 'equipTypeId',
  NAME = 'name',
  STORE = 'storeId',
  REMOTE_ACCESS = 'companyIdRemoteAccess',
  LOCATION = 'locationId'
};

enum InputStoreNames {
  NAME = 'storeName'
};

const initialValue = {
  value: '',
  isValid: true,
};

const initialEquipmentInputs: InputsType = {
  [InputEquipmentNames.NAME]: initialValue,
  [InputEquipmentNames.STORE]: initialValue,
  [InputEquipmentNames.REMOTE_ACCESS]: initialValue,
  [InputEquipmentNames.LOCATION]: initialValue,
};

const initialStoreInputs: InputsType = {
  [InputStoreNames.NAME]: initialValue
};

const getSubmitEquipmentData = (
  inputs: InputsType,
  equipment: Equipment,
  remoteAccessIDs: string,
  userId: Equipment,
  storeId?: number,
  locationId?: number,
  companyType?: string,
): UpdateEquipmentRequestType => ({
  equipment: {
    name: inputs[InputEquipmentNames.NAME].value,
    categoryId: 0,
    storeId: storeId ? storeId : +inputs[InputEquipmentNames.STORE].value,
    iokPin: equipment.iokPin,
    lastUpdate: getCurrentDateTimezone().slice(0, -1),
    idUser: userId.idUser,
    companyIdRemoteAccess: (companyType === 'Rede' || companyType === 'Dono') ? remoteAccessIDs : '',
    locationId: locationId ? locationId : +inputs[InputEquipmentNames.LOCATION].value,
  },
});

const getSubmitStoreData = (
  inputs: InputsType,
  companyId: number
): AddStoreRequestType => ({
  storeName: inputs[InputStoreNames.NAME].value,
  cnpj: '',
  zipCode: 0,
  street: '',
  streetNumber: 0,
  neighborhood: '',
  state: '',
  city: '',
  companyId: companyId,
  latitude: 0,
  longitude: 0,
});

type ModalProps = {
  token: string;
  equipment: any;
  user: any;
  companyId: number;
  onCancel: () => void;
  onUpdateList: () => void;
  companyOptions: any;
};

const ModalEditEquipment = (props: ModalProps) => {
  const intl = useIntl();
  const [isLoading, setIsLoading] = useState(false);
  const [showStoreForm, setShowStoreForm] = useState(false);
  const [formEquipmentState, inputEquipmentHandler, , , setSubmitted] = useForm(
    initialEquipmentInputs,
    false
  );
  const [formStoreState, inputStoreHandler] = useForm(
    initialStoreInputs,
    false
  );

  const { companyId, token, onCancel, onUpdateList } = props;
  const [showModalRemoteAccess, setShowModalRemoteAccess] = useState<boolean>();
  const [remoteAccessList, setRemoteAccessList] = useState<string>(props.equipment.companyIdRemoteAccess ?? "");
  const [hideModal, setHideModal] = useState<boolean>(false);
  const [initialRemoteAccess, setInitialRemoteAccess] = useState<string>(props.equipment.companyIdRemoteAccess === '1' ? '' : (props.equipment.companyIdRemoteAccess || ''));
  const [isCheckboxChecked, setIsCheckboxChecked] = useState<boolean>(String(props.equipment.companyIdRemoteAccess).split(';').includes('1') ?? false);
  const userDb = useLiveQuery(() => db.user.toArray())
  const companyDb = useLiveQuery(() => db.company.toArray())

  const nameRef = useRef<InputForwardType>(null);
  const storeNameRef = useRef<InputForwardType>(null);

  const [isLoadingGroup, setIsLoadingGroup] = useState(true);
  const [isLoadingLocation, setIsLoadingLocation] = useState(true);
  const [storesOptions, setStoresOptions] = useState<ListType<string | number>[]>([]);
  const [locationOptions, setLocationOptions] = useState<ListType<string | number>[]>([]);

  const loadStores = useCallback(async () => {
    try {
      const response = await loadStoresByCompanyId(props.user, token);
      const { stores } = response.data;

      const options = (stores ?? []).map(
        (store): ListType<string | number> => ({ value: store.id, label: store.storeName })
      );

      if (options.length === 0) {
        options.push({
          value: 0,
          label: intl.formatMessage({ id: 'Nenhum grupo criado' })
        });
      }

      setStoresOptions(options);
    } catch (error) {
      console.error(error);
    } finally {
      setIsLoadingGroup(false)
    }
  }, [companyId, intl, token]);

  const loadLocations = useCallback(async () => {
    try {
      const response = await loadLocationsByCompanyId(companyId, token);
      const { locations } = response.data;

      const filteredLocations = locations.filter(location => location.showLocation);

      const options = (filteredLocations ?? []).map(
        (location): ListType<string | number> => ({ value: location.id, label: location.locationName })
      );

      if (options.length === 0) {
        options.push({
          value: 0,
          label: intl.formatMessage({ id: 'equipment.Nenhuma localização criada' })
        })
      }

      setLocationOptions(options);

    } catch (error) {
      console.error(error);
    } finally {
      setIsLoadingLocation(false)
    }
  }, [companyId, intl, token]);

  useEffect(() => {
    if (companyId) {
      loadStores();
      loadLocations();
    }
  }, [companyId]);

  useEffect(() => {
    const loadInitialData = async () => {
      const ids = String(props.equipment.companyIdRemoteAccess)
        ?.split(';')
        .map((id: string) => id.trim())
        .filter((id: string) => id !== '');

      const filteredIds = ids?.filter((id: string) => id !== '1');
      const companyPromises = filteredIds.map((id) =>
        fetchCompanyNameById(id).then((company) =>
          company ? `${id} - ${company.corporateName}` : id
        )
      );
      const formattedValues = await Promise.all(companyPromises);

      const initialDisplayValue = formattedValues.join('; ') + (formattedValues.length > 0 ? ';' : '');
      setInitialRemoteAccess(initialDisplayValue);

      const numericIdsArray = [...filteredIds];
      if (String(props.equipment.companyIdRemoteAccess)?.split(';').includes('1')) {
        setIsCheckboxChecked(true);
      }
      setRemoteAccessList(numericIdsArray.join(';'));
    };

    loadInitialData();
  }, [props.equipment.companyIdRemoteAccess]);

  const touchAllStoreFields = () => {
    if (storeNameRef && storeNameRef.current)
      storeNameRef.current.touchHandler();
  };

  const loading = <Loading />;
  const isSelectsLoading = isLoadingGroup || isLoadingLocation;

  const handleCreateStore = useCallback(
    async (): Promise<number | undefined> => {
      if (!formStoreState.inputs[InputStoreNames.NAME].value.length) {
        toast.error(intl.formatMessage({
          id: 'store.não foi possível cadastrar o grupo. Tente novamente mais tarde',
        }).toUpperCase())
        return
      }
      const data = getSubmitStoreData(
        formStoreState.inputs,
        companyId
      );
      try {

        const response = await addNewStore(data, token);
        toast.success(
          intl.formatMessage({ id: 'store.grupo de equipamentos cadastrado com sucesso!' }).toUpperCase()
        );
        return response.data.id;
      } catch (error) {
        toast.error(
          intl.formatMessage({
            id: 'store.não foi possível cadastrar o grupo. Tente novamente mais tarde',
          }).toUpperCase()
        );
        return undefined;
      }
    },
    [companyId, formStoreState.inputs, intl, token]
  );

  const handleShowStoreForm = useCallback(() => {
    setShowStoreForm((prevState) => !prevState);
  }, [showStoreForm]);

  function extractIds(input: string): string {
    return input
      .split(";")
      .map(item => item.trim())
      .filter(Boolean)
      .map(item => {
        const [idPart] = item.split("-");
        const trimmedId = idPart.trim().toLowerCase();
        return trimmedId === "null" ? "null" : trimmedId;
      })
      .join(";");
  }

  const handleUpdateEquipment = useCallback(
    async (storeId?: number, locationId?: number): Promise<boolean> => {
      const data = getSubmitEquipmentData(
        formEquipmentState.inputs,
        props.equipment,
        formatIds(remoteAccessList),
        props.equipment,
        storeId,
        locationId,
        companyDb![0].companyType
      );
      try {
        data.equipment.storeId === 0 && delete data.equipment.storeId
        await updateEquipment(props.equipment.id, data, token);

        if (String(formatIds(remoteAccessList)) !== extractIds(initialRemoteAccess)) {
          setShowModalRemoteAccess(true);
          setHideModal(true);
        } else {
          onUpdateList();
          onCancel();
        }
        toast.success(
          intl.formatMessage({
            id: 'equipment.equipamento editado com sucesso!',
          }).toUpperCase()
        );
        return true;
      } catch (error) {
        toast.error(
          intl.formatMessage({
            id: 'equipment.não foi possível editar o equipamento. Tente novamente mais tarde',
          }).toUpperCase()
        );
        return false;
      }
    },
    [formEquipmentState.inputs, intl, token, props.equipment, userDb, companyDb, remoteAccessList, setHideModal]
  );

  const formatIds = (ids: string) => {
    return ids
      .trim()
      .replace(/\s+/g, '')
      .replace(/,+/g, ';')
      .replace(/;+/g, ';');
  };

  const handleCheckboxChange = (event: ChangeEvent<HTMLInputElement>) => {
    const isChecked = event.target.checked;
    setIsCheckboxChecked(isChecked);

    const remoteAccessField = document.getElementById(InputEquipmentNames.REMOTE_ACCESS) as HTMLInputElement;

    if (remoteAccessField) {
      let currentValue = remoteAccessField.value
        .split(';')
        .map(id => id.split('-')[0].trim())
        .filter(id => id !== '')
        .join(';');

      if (isChecked) {
        if (!currentValue.split(';').includes('1')) {
          currentValue = currentValue ? `${currentValue};1` : '1';
        }
      } else {
        currentValue = currentValue.split(';').filter(id => id !== '1').join(';');
      }

      setRemoteAccessList(currentValue);
      remoteAccessField.value = currentValue;

      const inputEvent = new Event('input', { bubbles: true });
      remoteAccessField.dispatchEvent(inputEvent);
    }
  };

  const handleRemoteAccessInput = ({ target }: React.ChangeEvent<HTMLInputElement>) => {
    setInitialRemoteAccess(target.value);
  };

  const handleChangeRemoteAccessInput = (value: string) => {
    setInitialRemoteAccess(formatIds(value));
    return formatIds(value);
  };

  const fetchCompanyNameById = async (id: string): Promise<Company | undefined> => {
    const response = await loadCompanyById(id);
    const company = response.data.company;
    if (!company) return;
    return company;
  };

  const handleBlurRemoteAccessInput = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const ids = initialRemoteAccess
      .split(';')
      .map(id => id.trim())
      .filter(id => id !== '');

    const uniqueIds = new Set<string>();
    const formattedValues: string[] = [];
    const invalidIds: string[] = [];

    for (const id of ids) {
      const [numericId, ...rest] = id.split('-');
      const trimmedId = numericId.trim();

      if (trimmedId !== '1' && !uniqueIds.has(trimmedId)) {
        const company = await fetchCompanyNameById(trimmedId);
        if (company && company.companyType === 'Distribuidor') {
          uniqueIds.add(trimmedId);
          formattedValues.push(`${trimmedId} - ${company.corporateName}`);
        } else {
          invalidIds.push(trimmedId);
        }
      }
    }

    const newValue = formattedValues.length > 0 ? formattedValues.join('; ') + ';' : '';
    setInitialRemoteAccess(newValue);

    const numericIdsArray = Array.from(uniqueIds);
    if (isCheckboxChecked && !numericIdsArray.includes('1')) numericIdsArray.push('1');

    const finalRemoteAccessList = numericIdsArray.length > 0 || isCheckboxChecked ? numericIdsArray.join(';') : '';
    setRemoteAccessList(finalRemoteAccessList);

    if (invalidIds.length > 0) {
      toast.error((intl.formatMessage({ id: "equipment.Os seguintes IDs não pertencem a uma companhia do tipo 'Distribuidor' e foram removidos" }) + ': ' + invalidIds.join(', ')).toUpperCase());
      const updatedIds = ids.filter(id => !invalidIds.includes(id.split('-')[0].trim())).join(';');
      event.target.value = updatedIds;
      setInitialRemoteAccess(updatedIds);
    }

    if (numericIdsArray.length === 0 && !isCheckboxChecked) {
      setInitialRemoteAccess('');
      setRemoteAccessList('');
    }
  };

  const handleFocusRemoteAccessInput = () => {
    const ids = initialRemoteAccess
      .split(';')
      .map(id => id.split('-')[0].trim())
      .filter(id => id !== '');

    setInitialRemoteAccess(ids.join(';') + (ids.length > 0 ? ';' : ''));
  };

  const handleSubmit = useCallback(
    async (event: FormEvent) => {
      let storeId: number | undefined = props.equipment.storeId;
      let locationId: number | undefined = props.equipment.locationId;
      event.preventDefault();
      setSubmitted(true);

      if (showStoreForm && storeId === null) {
        touchAllStoreFields();
        if (!formStoreState.isValid && !formEquipmentState.isValid) return;
        setIsLoading(true);
        storeId = await handleCreateStore();
        if (!storeId) {
          setIsLoading(false);
          return;
        }
      }

      if (!formEquipmentState.isValid) {
        setIsLoading(false);
        return;
      }
      if (storeId !== formEquipmentState.inputs.storeId.value && formEquipmentState.inputs.storeId.value) {
        storeId = formEquipmentState.inputs.storeId.value
      }
      if (formEquipmentState.inputs.locationId.value) {
        locationId = formEquipmentState.inputs.locationId.value;
      }
      const isSuccess = await handleUpdateEquipment(storeId, locationId);
      setIsLoading(false);
      if (!isSuccess) return;
    },
    [
      formEquipmentState.isValid,
      formStoreState.isValid,
      handleUpdateEquipment,
      handleCreateStore,
      onCancel,
      onUpdateList,
      showStoreForm,
    ]
  );

  const footer = (
    <>
      <div>
        <PrimaryButton
          type='submit'
          size='buttonModal'
          model='confirmation-gradient-modal'
          width='108px'
        >
          {intl.formatMessage({ id: 'store.salvar' })}
        </PrimaryButton>
      </div>
      <PrimaryButton
        type='button'
        size='buttonModal'
        model='cancellation'
        width='108px'
        onClick={props.onCancel}
      >
        {intl.formatMessage({ id: 'menus.CANCELAR' })}
      </PrimaryButton>
    </>
  );

  const content = (
    <Styled.Container>
      <Styled.Row>
        <Styled.Content flexGrow={1}>
          <InputForwardRef
            id={InputEquipmentNames.NAME}
            ref={nameRef}
            type='text'
            label={intl.formatMessage({ id: 'equipment.nome do equipamento' }) + ' *'}
            initialValid={true}
            initialValue={props.equipment.name}
            validators={[VALIDATOR_REQUIRE(), VALIDATOR_MAXLENGTH(22)]}
            maxLength={22}
            placeholder={intl.formatMessage({
              id: 'equipment.adicione um nome para facilitar seu reconhecimento',
            })}
            errorText={intl.formatMessage({
              id: 'equipment.preencha o nome do equipamento',
            })}
            onInput={inputEquipmentHandler}
          />
        </Styled.Content>
      </Styled.Row>
      <Styled.Row>
        <Styled.Content flexGrow={1}>
          <Select
            id={InputEquipmentNames.LOCATION}
            label={intl.formatMessage({ id: 'equipment.local' })}
            placeholder={intl.formatMessage({ id: 'equipment.selecionar' })}
            isLoading={isLoadingLocation}
            options={locationOptions}
            selectedOption={formEquipmentState.inputs.locationId.value || props.equipment.locationId}
            onSelectOptions={(value) => inputEquipmentHandler(InputEquipmentNames.LOCATION, value, true)}
          />
        </Styled.Content>
        <Styled.Content flexGrow={1}>
          <Select
            id={InputEquipmentNames.STORE}
            label={intl.formatMessage({ id: 'equipment.vinculado ao grupo' }) + ' *'}
            placeholder={intl.formatMessage({ id: 'equipment.selecionar' })}
            options={storesOptions}
            isLoading={isLoadingGroup}
            selectedOption={formEquipmentState.inputs.storeId.value || props.equipment.storeId}
            onSelectOptions={(value) => {
              const isValid = validate(String(value), [VALIDATOR_REQUIRE()]);
              inputEquipmentHandler(InputEquipmentNames.STORE, value, isValid)
            }}
            errors={(formEquipmentState.isSubmitted && !formEquipmentState.inputs.storeId.isValid) && {
              message: intl.formatMessage({ id: 'equipment.selecione um grupo' })
            }}
          />
        </Styled.Content>

        <Styled.Content className="AddGroup" flexBasis='140px'>
          <IconButton
            icon='ADD'
            label={intl.formatMessage({ id: 'equipment.cadastrar grupo' })}
            onClick={handleShowStoreForm}
            margin='25px 0 0 0'
          />
        </Styled.Content>

      </Styled.Row>
      <Styled.Row id={(companyDb && (companyDb[0].companyType === 'Rede' || companyDb[0].companyType === 'Dono')) ? '' : 'remoteAccess'}>
        <Styled.Content flexGrow={1} className='remote-access'>
          <InputForwardRef
            id={InputEquipmentNames.REMOTE_ACCESS}
            type='text'
            label={intl.formatMessage({ id: 'equipment.Acesso remoto' })}
            initialValid={true}
            initialValue={initialRemoteAccess}
            placeholder={intl.formatMessage({
              id: 'equipment.Informe o ID da empresa que poderá acessar remotamente seu forno. Use ; para separar IDs.',
            })}
            onInput={inputEquipmentHandler}
            onChangeInputSave={handleRemoteAccessInput}
            onBlur={handleBlurRemoteAccessInput}
            onFocus={handleFocusRemoteAccessInput}
            value={initialRemoteAccess}
            onChange={handleChangeRemoteAccessInput}
          />
          <Styled.Tooltip>
            <Styled.Img className="tooltip">
              <Question />
              <span className="tooltiptext">{intl.formatMessage({ id: 'equipment.O acesso remoto permite que as empresas selecionadas enviem menus e atualizações para seu equipamento.' })}</span>
            </Styled.Img>
          </Styled.Tooltip>
        </Styled.Content>
      </Styled.Row>

      <Styled.Row id={(companyDb && (companyDb[0].companyType === 'Rede' || companyDb[0].companyType === 'Dono')) ? '' : 'remoteAccess'}>
        <Styled.Content flexGrow={1}>
          <InputChecked
            id='pratica'
            type='checkbox'
            label={'equipment.Concedo acesso remoto à Prática oficial.'}
            name='pratica'
            initialValid
            value=' '
            onChecked={handleCheckboxChange}
            labelName='pratica'
            default={String(props.equipment.companyIdRemoteAccess)?.split(';').includes('1') ?? false}
            initialValue={String(props.equipment.companyIdRemoteAccess)?.split(';').includes('1') === true ? 1 : 0}
            checked={isCheckboxChecked}
          />
        </Styled.Content>
      </Styled.Row>

      {showStoreForm && (
        <>
          <Styled.Row>
            <Styled.Content flexGrow={1}>
              <InputForwardRef
                id={InputStoreNames.NAME}
                ref={storeNameRef}
                type='text'
                label={intl.formatMessage({ id: 'store.Nome do grupo' }) + ' *'}
                validators={[VALIDATOR_REQUIRE()]}
                placeholder={intl.formatMessage({
                  id: 'store.informe o nome do grupo',
                })}
                errorText={intl.formatMessage({
                  id: 'store.preencha o nome do grupo',
                })}
                onInput={inputStoreHandler}
              />
            </Styled.Content>
          </Styled.Row>
        </>
      )}
    </Styled.Container>
  );

  return (
    <>
      {showModalRemoteAccess && (
        <ModalRemoteAccess
          onCancel={() => {
            setShowModalRemoteAccess(false);
            onCancel();
            onUpdateList();
          }}
          companyIdRemoteAccess={formatIds(remoteAccessList)}
          equipmentId={props.equipment.id}
          onUpdateList={onUpdateList}
        />
      )}

      <Modal
        title={intl.formatMessage({ id: 'equipment.editar equipamento' })}
        style={{
          opacity: hideModal ? 0 : 1
        }}
        width='650px'
        children={isLoading || isSelectsLoading ? loading : content}
        footer={footer}
        onCancel={props.onCancel}
        onSubmit={handleSubmit}
      />
    </>
  );
};

export default ModalEditEquipment;
